import React, { useEffect, useState } from 'react';
import { Box, TextField, Alert, IconButton, InputAdornment, InputLabel } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import {
  handlePasswordResetSubmit,
  resetCurrentUser,
  updatePasswordResetFormField,
  resetPasswordResetForm,
} from '../../features/user/userSlice';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { askPasswordResetFromForm, resetPassword } from '../../features/user/utils';
import StyledBox from './styled/PasswordReset/StyledBox';
import PasswordVerification from './PasswordVerification';
import { Typography } from '../new/Typography';
import { Button } from '../new/Button';

const PasswordReset = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const formData = useSelector((state: RootState) => state.user.passwordResetForm);
  const errors = useSelector((state: RootState) => state.user.passwordResetFormErrors);

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [displaySuccessMsg, setDisplaySuccessMsg] = useState<boolean>(false);

  useEffect(() => {
    dispatch(resetPasswordResetForm());
    // Link flow: extract token from URL query param.
    const query = new URLSearchParams(location.search);
    const tokenParam = query.get('token');
    if (tokenParam) {
      dispatch(updatePasswordResetFormField({ fieldName: 'token', value: tokenParam }));
    }
  }, []);

  useEffect(() => {
    const hasErrors = Object.values(errors).some((error) => error !== '');
    if (!hasErrors && isSubmitted) {
      if (formData.token) {
        // Has token from link — submit new password
        dispatch(resetPassword()).then((result) => {
          if (resetPassword.fulfilled.match(result)) {
            setDisplaySuccessMsg(true);
            setTimeout(() => {
              dispatch(resetCurrentUser());
              navigate('/auth/sign-in');
            }, 1000 * 3);
          }
        });
      } else {
        // No token — request the reset link email
        dispatch(askPasswordResetFromForm());
      }
    }
  }, [errors, dispatch, isSubmitted]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    // true = validate email (step 1), false = validate password (step 2 via link)
    dispatch(handlePasswordResetSubmit(!formData.token));
    setIsSubmitted(false);
    setTimeout(() => setIsSubmitted(true), 0);
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault();

  return (
    <StyledBox>
      <Typography sx={{ marginTop: '1.875rem', marginBottom: '0.375rem' }} variant="h4">
        {formData.token ? 'Set New Password' : 'Reset Password'}
      </Typography>

      {formData.token ? (
        // Step 2: came from email link — just enter new password
        <React.Fragment>
          <Typography>Please enter your new password.</Typography>
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3, width: '60%' }}>
            <Typography
              sx={{ fontSize: '0.875rem', fontWeight: 600, fontFamily: 'Instrument Sans', lineHeight: '1.25rem', color: '#45464F', mb: 0.5 }}
            >
              Password
            </Typography>
            <TextField
              fullWidth
              id="newPassword"
              type={showPassword ? 'text' : 'password'}
              required
              sx={{ marginTop: 0, '& .MuiInputBase-input': { fontSize: '1rem', fontWeight: 400, fontFamily: 'Instrument Sans', lineHeight: '1.5rem', padding: '0.75rem 1rem' } }}
              autoFocus
              error={!!errors.newPasswordError}
              value={formData.newPassword}
              onChange={(e) =>
                dispatch(updatePasswordResetFormField({ fieldName: 'newPassword', value: e.target.value }))
              }
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <PasswordVerification password={formData.newPassword} errors={null} />
            <Button type="primary" sx={{ mt: 3, mb: 2 }} onClick={handleSubmit}>
              Save & Sign In
            </Button>
          </Box>
        </React.Fragment>
      ) : (
        // Step 1: enter email to receive the reset link
        <React.Fragment>
          <Typography sx={{ textAlign: 'center', width: '60%' }}>
            Enter the email associated with your account to receive a password reset link.
          </Typography>
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3, width: '60%' }}>
            <InputLabel htmlFor="email" sx={{ color: '#49454f', fontWeight: '500' }}>
              Email Address
            </InputLabel>
            <TextField
              fullWidth
              id="email"
              required
              placeholder="Add..."
              sx={{ marginTop: 0 }}
              autoFocus
              value={formData.email}
              disabled={formData.isRequested}
              onChange={(e) =>
                dispatch(updatePasswordResetFormField({ fieldName: 'email', value: e.target.value }))
              }
              error={!!errors.emailError}
              helperText={errors.emailError}
              margin="normal"
            />
            <Button onClick={(e) => handleSubmit(e)} type="primary" sx={{ mt: 3, mb: 3 }} disabled={formData.isRequested}>
              {formData.isLoading ? 'Sending email...' : 'Reset Password'}
            </Button>
            {formData.isRequested && (
              <Alert
                icon={<CheckCircleIcon sx={{ color: '#0f3010' }} fontSize="small" />}
                severity="success"
                sx={{ border: '0.0625rem solid #37ac71' }}
              >
                If an account exists for this email, you'll receive a password reset link shortly.
              </Alert>
            )}
          </Box>
        </React.Fragment>
      )}

      {errors.requestError && (
        <Alert sx={{ width: '60%' }} severity="error">
          {errors.requestError}
        </Alert>
      )}
      {displaySuccessMsg && (
        <Alert sx={{ width: '60%' }} severity="success">
          Password reset successfully. Redirecting to the Sign In page!
        </Alert>
      )}
    </StyledBox>
  );
};

export default PasswordReset;
